import { useAppReadyState } from '@/atoms/app-ready';
import { COLORS } from '@/colors';
import useElementSize from '@/hooks/use-element-size';
import { drawCircle } from '@/utils/canvas';
import { degToRad } from '@/utils/math';
import { mergeRefs } from '@/utils/merge-refs';
import { useAnimationFrame, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

interface Props {
    isPlaying: boolean;
}

const ServiceCardCanvas = ({ isPlaying }: Props) => {
    const ref = useRef<HTMLCanvasElement | null>(null);
    const delta = useRef(0);
    const [dpr, setDpr] = useState(1);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
    const [appReady] = useAppReadyState();
    const inView = useInView(ref, { margin: '-50px 0px -50px 0px' });
    const [canvasRef, size] = useElementSize<HTMLCanvasElement>();
    const width = (size?.width || 0) * dpr;
    const height = (size?.height || 0) * dpr;
    const dimension = Math.min(width, height) * (2 / dpr);
    const isDesktop = useMediaQuery('(min-width: 1800px) and (max-width: 1920px)');
    const isTouch = useMediaQuery('(max-width: 1200px)');
    const INNER_LINES_COUNT = isTouch ? 20 : 40;

    const getResponsiveDimension = (value: number) => {
        return (value / 1920) * dimension;
    };

    useEffect(() => {
        if (ref.current) {
            setCtx(ref.current.getContext('2d'));
        }
    }, [ctx]);

    useEffect(() => {
        const onResize = () => {
            setDpr(Math.min(window.devicePixelRatio, 2));
        };

        onResize();
        window.addEventListener('resize', onResize);

        return () => window.removeEventListener('resize', onResize);
    }, []);

    let centerX = width / 2;
    let centerY = height / 2;
    let k = 1.5;

    if (isDesktop) {
        k = 1.25;
    }

    const mdRadius = (getResponsiveDimension(830 * k) / 2) * dpr;
    const lgRadius = (getResponsiveDimension(896 * k) / 2) * dpr;
    const circleAnimationRadius = (getResponsiveDimension(641 * k) / 2) * dpr;
    const xsRadius = (getResponsiveDimension(155 * k) / 2) * dpr;
    const smRadius = (getResponsiveDimension(189 * k) / 2) * dpr;

    useAnimationFrame((t) => {
        const time = t * 0.00008;
        delta.current = time;
        const circleAngle = degToRad(90 + 540 * delta.current);
        if (appReady && ctx && inView && isPlaying) {
            ctx.clearRect(0, 0, width, height);
            ctx.strokeStyle = COLORS.WHITE_NOT_ACTIVE;

            drawCircle(centerX, centerY, mdRadius, ctx);
            drawCircle(centerX, centerY, lgRadius, ctx);

            drawCircle(
                centerX + Math.cos(circleAngle) * circleAnimationRadius,
                centerY + Math.sin(circleAngle) * circleAnimationRadius,
                smRadius,
                ctx,
            );
            drawCircle(
                centerX + Math.cos(circleAngle) * circleAnimationRadius,
                centerY + Math.sin(circleAngle) * circleAnimationRadius,
                xsRadius,
                ctx,
            );

            for (let i = 0; i < INNER_LINES_COUNT; i++) {
                const offset = i % 2 === 0 ? 0 : getResponsiveDimension(10);
                const angle = degToRad((i / INNER_LINES_COUNT) * 360) + Math.PI;
                const startX =
                    centerX +
                    Math.cos(circleAngle) * circleAnimationRadius +
                    Math.sin(angle) * (xsRadius + getResponsiveDimension(16) + offset);
                const startY =
                    centerY +
                    Math.sin(circleAngle) * circleAnimationRadius +
                    Math.cos(angle) * (xsRadius + getResponsiveDimension(16) + offset);
                const endX =
                    centerX +
                    Math.cos(circleAngle) * circleAnimationRadius +
                    Math.sin(angle) * (smRadius - getResponsiveDimension(16));
                const endY =
                    centerY +
                    Math.sin(circleAngle) * circleAnimationRadius +
                    Math.cos(angle) * (smRadius - getResponsiveDimension(16));
                ctx.moveTo(startX, startY);
                ctx.lineTo(endX, endY);
            }
            ctx.stroke();
        }
    });
    return <canvas className="service-card-canvas" ref={mergeRefs([ref, canvasRef])} width={width} height={height} />;
};

export default ServiceCardCanvas;
